<template>
  <!-- Custom Notification Modal -->
  <div v-if="notification.show" class="notification-overlay" @click="closeNotification">
    <div class="notification-modal" @click.stop :class="'notification-' + notification.type">
      <!-- Notification Header -->
      <div class="notification-header">
        <div class="notification-icon">
          <i v-if="notification.type === 'success'" class="bi bi-check-circle-fill"></i>
          <i v-else-if="notification.type === 'error'" class="bi bi-x-circle-fill"></i>
          <i v-else-if="notification.type === 'warning'" class="bi bi-exclamation-triangle-fill"></i>
          <i v-else class="bi bi-info-circle-fill"></i>
        </div>
        <div class="notification-content">
          <h4>{{ notification.title }}</h4>
          <p class="notification-message">{{ notification.message }}</p>
          <p v-if="notification.details" class="notification-details">{{ notification.details }}</p>
        </div>
        <button class="notification-close" @click="closeNotification">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      
      <!-- Copy Button if copyText is provided -->
      <div v-if="notification.copyText" class="notification-actions">
        <button 
          class="notification-button copy-button" 
          @click="copyToClipboard(notification.copyText)"
        >
          <i class="bi bi-clipboard"></i>
          Copy Text
        </button>
      </div>
      
      <!-- Action Buttons -->
      <div class="notification-footer">
        <button class="notification-button primary" @click="closeNotification">
          {{ notification.type === 'error' ? 'Understood' : 'OK' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useNotifications } from '@/composables/useNotifications'

export default {
  name: 'NotificationModal',
  setup() {
    const { notification, closeNotification } = useNotifications()

    const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text)
        // Show a brief success message
        const originalMessage = notification.message
        notification.message = '✅ Copied to clipboard!'
        setTimeout(() => {
          if (notification.show) {
            notification.message = originalMessage
          }
        }, 2000)
      } catch (error) {
        console.error('Failed to copy to clipboard:', error)
      }
    }

    return {
      notification,
      closeNotification,
      copyToClipboard
    }
  },
  mounted() {
    // Handle ESC key
    document.addEventListener('keydown', this.handleEscapeKey)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleEscapeKey)
  },
  methods: {
    handleEscapeKey(event) {
      if (event.key === 'Escape' && this.notification.show) {
        this.closeNotification()
      }
    }
  }
}
</script>

<style scoped>
/* Custom Notification Modal Styles */
.notification-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 20px;
  animation: fadeIn 0.3s ease-out;
}

.notification-modal {
  background: white;
  border-radius: 16px;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 500px;
  overflow: hidden;
  animation: slideUp 0.3s ease-out;
}

/* Notification Types */
.notification-success {
  border-top: 4px solid #10b981;
}
.notification-error {
  border-top: 4px solid #dc2626;
}
.notification-warning {
  border-top: 4px solid #f59e0b;
}
.notification-info {
  border-top: 4px solid #3b82f6;
}

/* Notification Header */
.notification-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
}

.notification-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.notification-success .notification-icon {
  background: #d1fae5;
  color: #10b981;
}
.notification-error .notification-icon {
  background: #fee2e2;
  color: #dc2626;
}
.notification-warning .notification-icon {
  background: #fef3c7;
  color: #f59e0b;
}
.notification-info .notification-icon {
  background: #dbeafe;
  color: #3b82f6;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-content h4 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.notification-message {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
}

.notification-details {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
}

.notification-close {
  background: #f3f4f6;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6b7280;
  flex-shrink: 0;
}

.notification-close:hover {
  background: #e5e7eb;
  color: #374151;
  transform: scale(1.05);
}

/* Notification Actions */
.notification-actions {
  padding: 0 24px 16px 24px;
  border-top: 1px solid #f3f4f6;
  padding-top: 16px;
}

/* Notification Footer */
.notification-footer {
  padding: 16px 24px 24px 24px;
  display: flex;
  justify-content: flex-end;
  background: #fafbfc;
  border-top: 1px solid #e5e7eb;
}

.notification-button {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  gap: 8px;
}

.notification-button.primary {
  background: #10b981;
  color: white;
  border-color: #10b981;
}

.notification-button.primary:hover {
  background: #059669;
  border-color: #059669;
  transform: translateY(-1px);
}

.notification-error .notification-button.primary {
  background: #dc2626;
  border-color: #dc2626;
}
.notification-error .notification-button.primary:hover {
  background: #b91c1c;
  border-color: #b91c1c;
}

.notification-warning .notification-button.primary {
  background: #f59e0b;
  border-color: #f59e0b;
}
.notification-warning .notification-button.primary:hover {
  background: #d97706;
  border-color: #d97706;
}

.copy-button {
  background: #f8fafc;
  color: #374151;
  border: 1px solid #d1d5db;
}

.copy-button:hover {
  background: #f1f5f9;
  border-color: #9ca3af;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .notification-overlay {
    padding: 10px;
  }
  
  .notification-header {
    padding: 20px;
  }
  
  .notification-content h4 {
    font-size: 16px;
  }
  
  .notification-footer {
    padding: 16px 20px 20px 20px;
  }
}
</style>